/**
 * View component for /user/register
 *
 * On successful registration this component forwards the user back to referrer
 * or to the root if there is no referrer.
 *
 * NOTE: upon reaching this page, user can toggle between /user/login and
 * /user/register without changing the original referring source route.
 */
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import UserLayout from '../components/UserLayout.jsx';
import UserRegisterForm from '../components/UserRegisterForm.jsx';
import { useLoggedInUser } from '../authService';
import apiUtils from '../../../global/utils/api';
import _ from "lodash";
import {testPasswordStrength, testValidEmail} from "../../../global/utils/helpUtils";
import {redirectToPage} from "../../../global/utils/routerUtils";

const UserRegister = () => {
  const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  const location = useLocation();
  const loggedInUser = useLoggedInUser();
  const params = new URLSearchParams(window.location.search);
  const email = params.get('email') || '';
    const isStudent = params.get('isStudent') || '';
  const [user, setUser] = useState({username: email, password: '', first_name: '', last_name: ''});
  const { redirect } = apiUtils.objectFromQueryString(location.search);
  const [errors, setErrors] = useState({email_error: '', password_error: '', first_name_error: '', last_name_error: '', organisation_error: ''});
  const [organisations, setOrganisations] = useState([]);
    const [items, setItems] = useState([
        {
            isTrue: false,
            text: 'Must be at least 8 characters',
        },
        {
            isTrue: false,
            text: 'Must contain one special character',
        },
    ]);
    const [disabled, setDisabled] = useState(true);

  useEffect(() => {
    const fetchOrganisations = async () => {
      try {
        const response = await apiUtils.callAPI('/api/organisations/', 'GET');
        setOrganisations(response);
      } catch (err) {
        console.error('Error fetching organisations:', err);
      }
    };

    fetchOrganisations();
  }, []);



  useEffect(() => {
    if(loggedInUser) {
      window.location.assign(redirect || "/learn")
    }
  }, [loggedInUser, redirect]);

  useEffect(() => {
    const newErrors = {...errors};
    if (user?.first_name && errors.first_name_error) {
      newErrors.first_name_error = '';
    }
    if (user?.last_name && errors.last_name_error) {
      newErrors.last_name_error = '';
    }
    if (user?.organisation && errors.organisation_error) {
      newErrors.organisation_error = '';
    }
    if (user?.username && errors.email_error) {
        newErrors.email_error = '';
    }
    if (user?.password) {
        const newItems = _.cloneDeep(items);
        for (const item of newItems) {
            if (item.text === 'Must be at least 8 characters') {
                item.isTrue = user?.password?.length >= 8;
            }
            if (item.text === 'Must contain one special character') {
                item.isTrue = testPasswordStrength(user?.password);
            }
        }
        setItems(newItems);
    }
    setErrors(newErrors);
    setDisabled(validateForm(user));
  }, [user])

    const validateForm = (user) => {
      if (!user?.organisation) {
          return true;
      }
      if (!(user?.password?.length >= 8)) {
          return true;
      } else if (!testPasswordStrength(user?.password)) {
          return true;
      }
      if (!user?.first_name?.length) {
          return true;
      }
      if (!user?.last_name?.length) {
          return true;
      }
      if (!user?.username?.length) {
          return true;
      } else if (!testValidEmail(user?.username)) {
          return true;
      }
      return false;
    }

  const handleSubmit = async e => {
    e.preventDefault();

    await handleNameErrors();
  }

  const withErrors = (obj) => {
      let res = false;
      for (const val of Object.values(obj)) {
          if (val) {
              res = true;
          }
      }
      return res;
  }

  const registerUser = async () => {
    const response = await apiUtils.callAPI('/api/users/register', 'POST', user);
    console.log({ response });
    if (response?.error) {
        await redirectToPage(`/user/register-error?errorCode=${response?.error?.code || ''}&email=${user?.username || ''}&errorMessage=${response?.error?.message || ''}`)
    } else if (response?.success) {
        await redirectToPage(`/user/register-success?email=${user?.username || ''}&successMessage=${'verify your email'}`);
    } else {
        handleThisErrors(response?.error?.message);
    }
  }

  const handleThisErrors = (err) => {
    const newErrors = {...errors};
    if (
        err?.message === 'Email address already in use. Try logging in.' ||
        err?.message === 'This user already exists. Please go to the login page and use your email.' ||
        err?.message === 'Invalid email address'
    ) {
      newErrors.email_error = err.message;
    } else {
      newErrors.email_error = '';
    }
    if (err?.message === 'Password strength requirements not met.') {
      newErrors.password_error = err.message;
    } else {
      newErrors.password_error = '';
    }
    if (err?.message === 'Organisation required') {
      newErrors.organisation_error = 'This field is required';
    } else {
      newErrors.organisation_error = '';
    }
    setErrors(newErrors);
  }

  const handleNameErrors = async () => {
    const newErrors = {...errors};
    const text = 'This field is required';
    if (!user?.first_name) {
      newErrors.first_name_error = text;
    } else {
      newErrors.first_name_error = '';
    }
    if (!user?.last_name) {
      newErrors.last_name_error = text;
    } else {
      newErrors.last_name_error = '';
    }
    if (!user?.organisation) {
      newErrors.organisation_error = text;
    } else {
      newErrors.organisation_error = '';
    }

    if (!user?.username) {
        newErrors.email_error = text;
    } else {
        newErrors.email_error = '';
        if (!re.test(user.username)) {
            newErrors.email_error = 'This not valid email';
        }
    }

    if (!withErrors(newErrors)) {
        await registerUser();
    }

    setErrors(newErrors);
  }

  return (
    <UserLayout title="Register" className={'h-full'}>
        <UserRegisterForm
            errors={errors}
            user={user}
            handleChange={e => setUser({...user, [e.target.name]: e.target.value})}
            handleSubmit={handleSubmit}
            redirectPath={location.search}
            organisations={organisations}
            items={items}
            disabled={disabled}
            isStudent={!!isStudent}
        />
    </UserLayout>
  )
}

export default UserRegister
